import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import {
  Send,
  ArrowLeft,
  MessageCircle,
  Search,
  Circle,
  UserCheck,
  ChevronLeft
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function Messages() {
  const { user, isActiveMember, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startWith = searchParams.get('with');

  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [friends, setFriends] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/community/messages/conversations`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
        return data;
      }
    } catch (e) { console.error('Error fetching conversations:', e); }
    finally { setLoading(false); }
    return [];
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    if (!convId) return;
    setLoadingMessages(true);
    try {
      const res = await fetch(`${API}/community/messages/conversations/${convId}/messages`, { credentials: 'include' });
      if (res.ok) {
        setMessages(await res.json());
        await fetch(`${API}/community/messages/conversations/${convId}/read`, { method: 'PUT', credentials: 'include' });
        setConversations(prev => prev.map(c => c.conversation_id === convId ? { ...c, unread: 0 } : c));
      }
    } catch (e) { console.error('Error fetching messages:', e); }
    finally { setLoadingMessages(false); }
  }, []);

  const fetchFriends = useCallback(async () => {
    try {
      const res = await fetch(`${API}/community/friends`, { credentials: 'include' });
      if (res.ok) setFriends(await res.json());
    } catch {}
  }, []);

  const startConversation = useCallback(async (friendUserId) => {
    try {
      const res = await fetch(`${API}/community/messages/conversations/${friendUserId}`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const conv = await res.json();
        setActiveConvId(conv.conversation_id);
        setActiveConv(conv);
        setShowNewChat(false);
        fetchConversations();
        fetchMessages(conv.conversation_id);
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Cannot start conversation');
      }
    } catch { toast.error('Failed to start conversation'); }
  }, [fetchConversations, fetchMessages]);

  useEffect(() => {
    if (isActiveMember) {
      fetchConversations().then((convs) => {
        if (startWith) {
          startConversation(startWith);
        }
      });
      fetchFriends();
    } else {
      setLoading(false);
    }
  }, [isActiveMember]);

  useEffect(() => {
    if (activeConvId) {
      fetchMessages(activeConvId);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => {
        fetchMessages(activeConvId);
        fetchConversations();
      }, 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeConvId]);

  useEffect(() => { scrollToBottom(); }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || !activeConvId) return;
    setSending(true);
    const msgContent = newMessage;
    setNewMessage('');
    try {
      const res = await fetch(`${API}/community/messages/conversations/${activeConvId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: msgContent })
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages(prev => [...prev, msg]);
        fetchConversations();
      } else {
        setNewMessage(msgContent);
        toast.error('Failed to send message');
      }
    } catch {
      setNewMessage(msgContent);
      toast.error('Failed to send');
    }
    finally { setSending(false); }
  };

  const selectConversation = (conv) => {
    setActiveConvId(conv.conversation_id);
    setActiveConv(conv);
    setShowNewChat(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D4F7A]"></div>
      </div>
    );
  }

  if (!isActiveMember) {
    navigate('/community');
    return null;
  }

  const filteredFriends = friends.filter(f =>
    !searchQuery || f.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const existingConvFriendIds = conversations.map(c => c.friend?.user_id);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="messages-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#3D4F7A] to-[#2A3755] py-6">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          <div className="flex items-center gap-4">
            <Link to="/community" className="text-white/70 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">Messages</h1>
              <p className="text-slate-300 text-sm">Direct messages with your friends</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl py-6">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 220px)' }}>
          <div className="flex h-full">
            {/* Conversations Sidebar */}
            <div className={`w-full md:w-80 border-r border-slate-200 flex flex-col ${activeConvId ? 'hidden md:flex' : 'flex'}`} data-testid="conversations-list">
              <div className="p-4 border-b border-slate-100">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-slate-900">Chats</h2>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setShowNewChat(!showNewChat); setSearchQuery(''); }} data-testid="new-chat-btn">
                    <MessageCircle className="w-3 h-3 mr-1" />New
                  </Button>
                </div>
                {showNewChat && (
                  <div data-testid="new-chat-search">
                    <div className="relative mb-2">
                      <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search friends..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3D4F7A]/20"
                        autoFocus
                        data-testid="new-chat-search-input"
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {filteredFriends.length === 0 ? (
                        <p className="text-xs text-slate-500 text-center py-2">
                          {friends.length === 0 ? 'Add friends first to start messaging' : 'No matching friends'}
                        </p>
                      ) : (
                        filteredFriends.map((f) => (
                          <button
                            key={f.user_id}
                            onClick={() => startConversation(f.user_id)}
                            className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors text-left"
                            data-testid={`start-chat-${f.user_id}`}
                          >
                            <div className="relative">
                              <Avatar className="w-8 h-8">
                                <AvatarImage src={f.picture} />
                                <AvatarFallback className="bg-[#3D4F7A] text-white text-xs">{f.name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              {f.is_online && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full ring-2 ring-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{f.name}</p>
                              {f.user_role && <p className="text-[10px] text-slate-500">{f.user_role}</p>}
                            </div>
                            {existingConvFriendIds.includes(f.user_id) && (
                              <span className="text-[10px] text-slate-400">opened</span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Conversation Items */}
              <div className="flex-1 overflow-y-auto" data-testid="conversation-items">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3D4F7A]"></div>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <MessageCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500 mb-1">No conversations yet</p>
                    <p className="text-xs text-slate-400">Start chatting with a friend!</p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <button
                      key={conv.conversation_id}
                      onClick={() => selectConversation(conv)}
                      className={`w-full flex items-center gap-3 p-3 border-b border-slate-50 transition-colors text-left ${
                        activeConvId === conv.conversation_id ? 'bg-[#3D4F7A]/5 border-l-2 border-l-[#3D4F7A]' : 'hover:bg-slate-50'
                      }`}
                      data-testid={`conversation-${conv.conversation_id}`}
                    >
                      <div className="relative">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={conv.friend?.picture} />
                          <AvatarFallback className="bg-[#3D4F7A] text-white text-sm">{conv.friend?.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        {conv.is_online && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full ring-2 ring-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-slate-900 truncate">{conv.friend?.name}</p>
                          <span className="text-[10px] text-slate-400 flex-shrink-0 ml-2">{formatTime(conv.last_message_at)}</span>
                        </div>
                        <p className="text-xs text-slate-500 truncate mt-0.5">{conv.last_message || 'No messages yet'}</p>
                      </div>
                      {conv.unread > 0 && (
                        <span className="bg-[#D42B3C] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" data-testid={`unread-badge-${conv.conversation_id}`}>
                          {conv.unread}
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className={`flex-1 flex flex-col ${!activeConvId ? 'hidden md:flex' : 'flex'}`}>
              {activeConv ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-slate-200 flex items-center gap-3 bg-white" data-testid="chat-header">
                    <button onClick={() => { setActiveConvId(null); setActiveConv(null); }} className="md:hidden text-slate-500 hover:text-slate-700">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div className="relative">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={activeConv.friend?.picture} />
                        <AvatarFallback className="bg-[#3D4F7A] text-white">{activeConv.friend?.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      {activeConv.is_online && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full ring-2 ring-white" />}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{activeConv.friend?.name}</p>
                      <div className="flex items-center gap-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${activeConv.is_online ? 'bg-green-500' : 'bg-slate-300'}`} />
                        <span className="text-xs text-slate-500">{activeConv.is_online ? 'Online' : 'Offline'}</span>
                      </div>
                    </div>
                    <div className="ml-auto">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#3D4F7A]/10 text-[#3D4F7A] text-[10px] font-medium">
                        <UserCheck className="w-2.5 h-2.5" />Friend
                      </span>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="messages-thread">
                    {loadingMessages ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3D4F7A]"></div>
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="text-center py-12">
                        <MessageCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No messages yet</p>
                        <p className="text-xs text-slate-400 mt-1">Say hello to {activeConv.friend?.name}!</p>
                      </div>
                    ) : (
                      messages.map((msg) => {
                        const isMe = msg.sender_id === user?.user_id;
                        return (
                          <div key={msg.message_id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-testid={`message-${msg.message_id}`}>
                            <div className={`flex items-end gap-2 max-w-[75%] ${isMe ? 'flex-row-reverse' : ''}`}>
                              {!isMe && (
                                <Avatar className="w-7 h-7 flex-shrink-0">
                                  <AvatarImage src={msg.sender_picture} />
                                  <AvatarFallback className="bg-[#3D4F7A] text-white text-[10px]">{msg.sender_name?.charAt(0)}</AvatarFallback>
                                </Avatar>
                              )}
                              <div>
                                <div className={`px-3.5 py-2.5 rounded-2xl ${
                                  isMe
                                    ? 'bg-[#3D4F7A] text-white rounded-br-md'
                                    : 'bg-white text-slate-900 border border-slate-200 rounded-bl-md'
                                }`}>
                                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                                </div>
                                <p className={`text-[10px] text-slate-400 mt-0.5 ${isMe ? 'text-right' : ''}`}>
                                  {formatTime(msg.created_at)}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <div className="p-4 border-t border-slate-200 bg-white" data-testid="message-input-area">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                        placeholder={`Message ${activeConv.friend?.name}...`}
                        className="flex-1 px-4 py-2.5 text-sm border border-slate-200 rounded-full focus:outline-none focus:ring-2 focus:ring-[#3D4F7A]/20 focus:border-[#3D4F7A]"
                        disabled={sending}
                        data-testid="message-input"
                      />
                      <Button
                        onClick={handleSend}
                        disabled={!newMessage.trim() || sending}
                        className="bg-[#3D4F7A] hover:bg-[#2A3755] rounded-full w-10 h-10 p-0"
                        data-testid="send-message-btn"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center bg-slate-50">
                  <div className="text-center">
                    <MessageCircle className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">Your Messages</h3>
                    <p className="text-sm text-slate-500 mb-4">Select a conversation or start a new chat</p>
                    <Button onClick={() => setShowNewChat(true)} className="bg-[#3D4F7A] hover:bg-[#2A3755]" data-testid="start-new-chat-cta">
                      <MessageCircle className="w-4 h-4 mr-2" />New Message
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
